// src/pages/AdminCategoriasList.js
import React, { useState, useEffect, useRef } from 'react';
import { db, storage } from '../firebase';
import { collection, getDocs, addDoc, doc, updateDoc, deleteDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";

// Importações do Editor Tiptap
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import EditorToolbar from '../components/EditorToolbar';
import './PostEditor.css'; // Reutilizando estilos do editor

import '../components/AdminModals.css';

const AdminCategoriasList = () => {
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [currentCategory, setCurrentCategory] = useState(null);
    const [formData, setFormData] = useState({});
    const [isUploading, setIsUploading] = useState(false);
    const fileInputRef = useRef(null);

    // --- CONFIGURAÇÃO DO EDITOR ---
    const editor = useEditor({
        extensions: [StarterKit],
        content: '',
        onUpdate: ({ editor }) => {
            // Atualiza o campo 'description' no formulário com o HTML do editor
            setFormData(prev => ({ ...prev, description: editor.getHTML() }));
        },
    });

    // Função para gerar o slug
    const generateSlug = (title) => title.toLowerCase().replace(/ /g, '-').replace(/[^\w-]+/g, '');

    // Efeito para gerar o slug automaticamente
    useEffect(() => {
        if (formData.name && !currentCategory) {
            setFormData(prev => ({ ...prev, slug: generateSlug(prev.name) }));
        }
    }, [formData.name, currentCategory]);

    const fetchCategories = async () => {
        setLoading(true);
        const collectionPath = "pts-com-br/fabricantes/novotechnik_categories";
        const snapshot = await getDocs(collection(db, collectionPath));
        setCategories(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
        setLoading(false);
    };

    useEffect(() => {
        fetchCategories();
    }, []);
    
    // Efeito para carregar o conteúdo no editor ao abrir o modal
    useEffect(() => {
        if (editor && isModalOpen) {
            editor.commands.setContent(formData.description || '');
        }
    }, [editor, isModalOpen, formData.description]);

    const handleOpenModal = (category = null) => {
        setCurrentCategory(category);
        setFormData(category || { name: '', slug: '', mainMedia: '', description: '' });
        setIsModalOpen(true);
    };

    const handleCloseModal = () => setIsModalOpen(false);

    const handleFormChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleImageUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;
        setIsUploading(true);
        
        // Define o caminho no Storage
        const storageRef = ref(storage, `novotechnik/categories/${file.name}`);
        
        try {
            // 1. Faz o upload do arquivo
            const snapshot = await uploadBytes(storageRef, file);
            // 2. Pega a URL de download
            const downloadURL = await getDownloadURL(snapshot.ref);
            // 3. Atualiza o estado do formulário com a nova URL
            setFormData(prev => ({ ...prev, mainMedia: downloadURL }));
            alert("Imagem enviada com sucesso!");
        } catch (error) {
            console.error("Erro no upload da imagem:", error);
            alert("Ocorreu um erro ao enviar a imagem. Verifique as regras do Storage.");
        } finally {
            setIsUploading(false);
        }
    };

    const handleSaveCategory = async (e) => {
        e.preventDefault();
        const collectionPath = "pts-com-br/fabricantes/novotechnik_categories";
        try {
            if (currentCategory) {
                const docRef = doc(db, collectionPath, currentCategory.id);
                await updateDoc(docRef, formData);
            } else {
                await addDoc(collection(db, collectionPath), { ...formData, productCount: 0 });
            }
            fetchCategories();
            handleCloseModal();
        } catch (err) {
            console.error("Erro ao salvar:", err);
        }
    };

    const handleDeleteCategory = async (category) => {
        if ((category.productCount || 0) > 0) {
            alert(`Não é possível apagar. A categoria "${category.name}" contém produtos.`);
            return;
        }
        if (window.confirm(`Apagar a categoria "${category.name}"?`)) {
            try {
                const docRef = doc(db, "pts-com-br/fabricantes/novotechnik_categories", category.id);
                await deleteDoc(docRef);
                fetchCategories();
            } catch (err) {
                console.error("Erro ao apagar:", err);
            }
        }
    };

    return (
        <div className="admin-list-container">
            <div className="admin-list-header">
                <h1>Gerenciamento de Categorias</h1>
                <button onClick={() => handleOpenModal()} className="create-button">
                    + Nova Categoria
                </button>
            </div>

            {loading ? <p>Carregando...</p> : (
                <div className="table-wrapper">
                    <table>
                        <thead>
                            <tr>
                                <th>Nome da Categoria</th>
                                <th>Nº de Produtos</th>
                                <th>Ações</th>
                            </tr>
                        </thead>
                        <tbody>
                            {categories.map(cat => (
                                <tr key={cat.id}>
                                    <td>{cat.name}</td>
                                    <td>{cat.productCount || 0}</td>
                                    <td className="actions-cell">
                                        <button onClick={() => handleOpenModal(cat)} className="action-button edit-button">Editar</button>
                                        <button onClick={() => handleDeleteCategory(cat)} className="action-button delete-button">Apagar</button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {isModalOpen && (
                <div className="modal-overlay">
                    <div className="modal-content large">
                        <h2>{currentCategory ? 'Editar Categoria' : 'Nova Categoria'}</h2>
                        <form onSubmit={handleSaveCategory}>
                            <div className="form-group">
                                <label>Nome da Categoria</label>
                                <input type="text" name="name" value={formData.name || ''} onChange={handleFormChange} required />
                            </div>
                            <div className="form-group">
                                <label>Slug (URL)</label>
                                <input type="text" name="slug" value={formData.slug || ''} onChange={handleFormChange} required />
                            </div>
                            
                            <div className="form-group">
                                <label>Imagem Principal da Categoria</label>
                                <input 
                                    type="file" 
                                    ref={fileInputRef} 
                                    onChange={handleImageUpload} 
                                    style={{ display: 'none' }} 
                                    accept="image/*" 
                                />
                                <button 
                                    type="button" 
                                    className="upload-button" 
                                    onClick={() => fileInputRef.current.click()} 
                                    disabled={isUploading}
                                >
                                    {isUploading ? 'Enviando...' : 'Escolher Imagem'}
                                </button>
                                {formData.mainMedia && <img src={formData.mainMedia} alt="Preview" className="image-preview" />}
                            </div>
                            
                            {/* --- CAMPO DE DESCRIÇÃO COM EDITOR RICH TEXT --- */}
                            <div className="form-group">
                                <label>Descrição da Categoria</label>
                                <div className="editor-wrapper">
                                    <EditorToolbar editor={editor} />
                                    <EditorContent editor={editor} />
                                </div>
                            </div>

                            <div className="modal-actions">
                                <button type="button" onClick={handleCloseModal} className="button-cancel">Cancelar</button>
                                <button type="submit" className="button-save" disabled={isUploading}>Salvar</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AdminCategoriasList;